import PropTypes from "prop-types";
import React from "react";

// Styles
import "./styles.css";

// Default sections
import { DefaultCommissionTier, DefaultCommissionsDefinition, DefaultCommissionsTier } from "@/PayPlans/helpers/DefaultSections";

// Utils
import {
	isDisabledSwitch,
	getSmallestMatrixDimensions,
	resizeTabs,
	shrinkMakes,
	getMatrixDimensions,
	getMatrixDimensionsForTab,
	expandMakes,
	CONSTANTS,
	getDefaultCommissionsFromModel,
} from "./utils";

// Helpers
import { clone } from "@/Common/helpers/Modifiers/formatters";
import { getErrorKey } from "@/Common/helpers/Modifiers/characters";
import { isSimilarObject } from "@/Common/helpers/Verifications/objects";
import { setObjectsList } from "@/Common/helpers/objects";
import { removeListValue } from "@/Common/helpers/Arrays";

// Layouts
import TabLoader from "@/Layouts/components/Skeletons/TabLoader";
import DefaultTiersLoader from "@/Layouts/components/Skeletons/DefaultTiersLoader/DefaultTiersLoader";
import TabSelectable from "@/Layouts/components/Tab/TabSelectable";
import CustomizedSwitches from "@/Layouts/components/InputComponents/CustomizedSwitches";

// Custom components
import CommissionSection from "./CommissionSection";

// MUI
import { Grid } from "@mui/material";

const tabSettings = ["combineFranchises", "globalUnitCounts", "newAndPreOwnedRequired", "combineOEMs"];
const potentialKeys = ["newVehicles", "preOwned", "finance"];
const excludeFromTopSwitches = ["finance"];
const excludeFromCombineTabs = ["preOwned", "finance"];

const setTabNames = new Map([
	["newVehicles", "New Vehicles"],
	["preOwned", "Pre-Owned Vehicles"],
	["finance", "F&I"],
]);

const getIsValid = (errors, sectionId) => {
	if (!errors?.get(sectionId)) {
		return true;
	}
	return !errors?.get(sectionId)?.length;
};

const getPotentialTabsErrorsKeys = (section, tabs = []) => tabs?.map((tab) => getErrorKey(section, tab?.key));

const getErrorsToRemove = (errors = [], errorsToKeep = []) => {
	if (errors.length) {
		return errors.filter((key) => !errorsToKeep.includes(key));
	}
	return [];
};

const deleteUnnecessaryKeys = (object) => {
	for (const [key, value] of Object.entries(object)) {
		if (!value || JSON.stringify(value) === "[]" || JSON.stringify(value) === "{}") {
			delete object[key];
		}
	}
	if (!object || JSON.stringify(object) === "[]" || JSON.stringify(object) === "{}") {
		return null;
	}
	return object;
};

const sanitizeObject = (object) => {
	if (!object || JSON.stringify(object) === "[]" || JSON.stringify(object) === "{}") {
		return null;
	}
	object = deleteUnnecessaryKeys(object);
	if (typeof object === "object") {
		for (const [key, value] of Object.entries(object)) {
			object[key] = deleteUnnecessaryKeys(value);
			object[key] = sanitizeObject(object[key]);
		}
		return deleteUnnecessaryKeys(object);
	}
	return object;
};

const getOEMsGrossStructure = (OEMs = [], checked = false) => {
	if (!OEMs?.length) {
		return [];
	}
	const uniqueOEMs = setObjectsList(OEMs, ["oemReference"]);
	return uniqueOEMs?.map((oem) => {
		return { make: oem?.oemReference, dealerCodeFS: oem?.dealerCodeFS, checked };
	});
};

const isNoCommissionException = (model) => {
	const tabs = model?.tabs || [];
	const evaluatesCommission = tabs.some((tab) =>
		tab.makes.some((make) => make?.tiers?.some((tier) => tier.commissions.some((commission) => commission.commission.value)))
	);
	return !evaluatesCommission;
};

export default function DefaultCommissions(props) {
	const {
		sectionId = "default",
		content = null,
		franchises = [],
		oems = [],
		loading = false,
		readOnly = false,
		useSectionsErrors = () => {},
		onActionUpdate = () => {},
	} = props;

	// Hooks
	const { errors, addSectionError, addSectionErrors, removeSectionError, removeSectionErrors, clearSectionErrors } =
		useSectionsErrors?.() || {};

	// States
	// eslint-disable-next-line no-unused-vars
	const [tabIndex, setTabIndex] = React.useState(0);

	const [model, setModel] = React.useState(null);
	const matrixDimensions = getMatrixDimensions(model?.tabs || []);

	const validateAgainst0 = isNoCommissionException(model);

	const financeTab = model?.tabs?.find((tab) => tab?.key === "finance");
	const financeTabActive = Boolean(financeTab === model?.tabs?.[tabIndex]);

	const maximumColumnsInput = !financeTabActive ? CONSTANTS.MAX_COLUMNS_INPUT : CONSTANTS.DEFAULT_BACKEND_PRODUCTS;

	const wasUpdated = React.useRef(false);
	const saveErrors = React.useRef([]);

	// Error handler
	const tabsErrors = React.useMemo(() => {
		if (errors.size && model?.tabs) {
			const localErrors = Array.from(Array(model.tabs.length)).map(() => false);
			const errorsToKeep = getPotentialTabsErrorsKeys(sectionId, model.tabs);
			const listErrors = errors?.get(sectionId) || []; // Ids that have errors

			errorsToKeep.forEach((errorKey, index) => {
				const included = listErrors.includes(errorKey) || saveErrors?.current?.includes(errorKey);
				localErrors[index] = Boolean(included);
			});
			return localErrors;
		}
		return [];
	}, [sectionId, errors, model]);

	const resetTabsErrors = React.useCallback(
		(localSectionId, tabs, tabsToExclude) => {
			if (tabs?.length) {
				const tabsToExcludeIds = tabsToExclude.map((key) => getErrorKey(localSectionId, key));
				const errorsToKeep = getPotentialTabsErrorsKeys(localSectionId, tabs)?.filter((id) => !tabsToExcludeIds.includes(id));
				const errorsToRemove = getErrorsToRemove(errors?.get(localSectionId), errorsToKeep)?.filter(
					(id) => !tabsToExcludeIds.includes(id)
				);
				removeSectionErrors(localSectionId, errorsToRemove);
				addSectionErrors(localSectionId, errorsToKeep);
			}
		},
		[errors, removeSectionErrors, addSectionErrors]
	);

	// Actions handlers
	const update = (section, data, isValid = false) => {
		if (isValid && wasUpdated.current && Boolean(model)) {
			const commissionsObj = getDefaultCommissionsFromModel(data, franchises);
			onActionUpdate?.(section, null, commissionsObj, isValid);
		} else if (data) {
			setModel(data);
		}
	};

	const handleUpdateTiers = (section, key, data, isValidSection) => {
		if (isValidSection) {
			saveErrors.current = removeListValue(saveErrors.current, getErrorKey(sectionId, key));
		}
		handleValidation(section, key, isValidSection);
		let updateModel = { ...model };
		for (let i = 0; i < updateModel?.tabs?.length; i++) {
			if (updateModel?.tabs[i]?.key === key) {
				updateModel.tabs[i].makes = data;
				break;
			}
		}
		const newMatrixDimensionsForTab = getMatrixDimensionsForTab({
			makes: data,
		});
		matrixDimensions.set(key, newMatrixDimensionsForTab);

		// resize tabs if (new and preowned required is activated), and (the tab has not been explicitly prevented from doing so)
		if (Boolean(updateModel?.tabsProps?.newAndPreOwnedRequired) && !excludeFromTopSwitches.includes(key)) {
			for (const [tab, dimensions] of matrixDimensions) {
				if (!excludeFromTopSwitches.includes(tab) && tab !== key && !isSimilarObject(newMatrixDimensionsForTab, dimensions)) {
					for (let i = 0; i < updateModel?.tabs?.length; i++) {
						if (updateModel?.tabs[i]?.key === tab) {
							updateModel.tabs[i].makes = resizeMakes(updateModel.tabs[i].makes, dimensions, newMatrixDimensionsForTab);
							addSectionError(section, key);
							break;
						}
					}
				}
			}
		} else if (
			excludeFromTopSwitches.includes(key) &&
			Boolean(updateModel?.tabs?.find((tab) => tab?.key === key)?.["newAndPreOwnedRequired"])
		) {
			let makes = updateModel?.tabs?.find((tab) => tab?.key === key).makes;
			makes = resizeMakes(makes, matrixDimensions.get(key), newMatrixDimensionsForTab);
			addSectionError(section, key);
			updateModel.tabs.find((tab) => tab?.key === key).makes = makes;
		}

		wasUpdated.current = true;
		updateModel = clone(updateModel);
		update(section, updateModel);
	};

	const selectCorrectTab = (combineFranchisesSwitchState) => {
		const financeTabIsActive = model?.tabs?.[tabIndex]?.tabName === "F&I";
		const preOwnedTabIsActive = model?.tabs?.[tabIndex]?.tabName === "Pre-Owned Vehicles";
		if (financeTabIsActive) {
			if (combineFranchisesSwitchState) {
				setTabIndex(2);
			} else {
				setTabIndex(model.tabs.length + 1);
			}
		} else if (preOwnedTabIsActive) {
			if (combineFranchisesSwitchState) {
				setTabIndex(1);
			} else {
				setTabIndex(franchises.length);
			}
		} else {
			setTabIndex(0);
		}
	};

	const handleUpdateSwitches = (section, area, value, currentTabIndex = null) => {
		let updatedModel = { ...model };

		if (area === "applyTiersRetroactively") {
			const tab = updatedModel["tabs"]?.[currentTabIndex] || null;
			if (tab?.makes?.[0]) {
				tab.makes[0] = {
					...tab.makes[0],
					applyTiersRetroactively: value,
				};
			}
		} else {
			updatedModel["tabsProps"][area] = value;
		}

		const combineFranchises = updatedModel["tabsProps"]["combineFranchises"];
		switch (area) {
			case "combineFranchises":
				updatedModel.tabs = getCombinedTabs(clone(updatedModel.tabs), franchises, combineFranchises);
				resetTabsErrors(section, updatedModel["tabs"], excludeFromCombineTabs);
				selectCorrectTab(value);
				break;
			case "newAndPreOwnedRequired": {
				if (value) {
					if (franchises?.length) {
						updatedModel["tabsProps"]["combineFranchises"] = true;
					}
					updatedModel.tabs = getCombinedTabs(
						clone(updatedModel.tabs),
						franchises,
						updatedModel["tabsProps"]["combineFranchises"]
					);

					if (updatedModel["tabsProps"]["globalUnitCounts"]) {
						updatedModel["tabsProps"]["globalUnitCounts"] = false;
					}

					resetTabsErrors(section, updatedModel["tabs"], excludeFromCombineTabs);
					const smallestMatrixDimensions = getSmallestMatrixDimensions(matrixDimensions);
					updatedModel.tabs = resizeTabs(updatedModel?.tabs, smallestMatrixDimensions, maximumColumnsInput);
					selectCorrectTab(updatedModel["tabsProps"]["combineFranchises"]);
				}
				break;
			}
			case "globalUnitCounts": {
				updatedModel["tabsProps"][area] = value;
				break;
			}
			default:
				break;
		}
		wasUpdated.current = true;
		updatedModel = clone(updatedModel);
		update(section, updatedModel);
	};

	const handleUpdateFinanceSwitch = (section, area, value) => {
		let updatedModel = { ...model };
		let localFinanceTab = model?.tabs?.find((tab) => tab?.key === "finance");
		let combineApplied = area === "combineOEMs" && value;
		localFinanceTab[area] = value;
		const combineOEMs = updatedModel["combineOEMs"];

		const newMatrixDimensionsForTab = getMatrixDimensionsForTab({
			makes: localFinanceTab["makes"],
		});

		switch (area) {
			case "newAndPreOwnedRequired": {
				if (value) {
					localFinanceTab["globalUnitCounts"] = false;

					if (!combineOEMs && oems?.length > 1) {
						localFinanceTab["combineOEMs"] = true;
						combineApplied = true;
					}

					const smallestMatrixDimensions = clone(newMatrixDimensionsForTab);
					const { minTiers, minColumns } = smallestMatrixDimensions;
					newMatrixDimensionsForTab.maxTiers = minTiers;
					newMatrixDimensionsForTab.maxColumns = minColumns;

					localFinanceTab["makes"] = resizeMakes(localFinanceTab["makes"], newMatrixDimensionsForTab, smallestMatrixDimensions);
				}
				break;
			}
			case "globalUnitCounts": {
				if (Boolean(value) && oems?.length > 1) {
					localFinanceTab["combineOEMs"] = true;
				} else if (!localFinanceTab?.variableGrossStructure?.newFinance) {
					localFinanceTab["combineOEMs"] = false;
				}
				break;
			}
			default:
				break;
		}

		matrixDimensions.set("finance", newMatrixDimensionsForTab);
		localFinanceTab = updateFinanceMakes(localFinanceTab);
		for (let i = 0; i < model?.tabs?.length; i++) {
			if (updatedModel.tabs[i]?.key === "finance") {
				updatedModel.tabs[i] = localFinanceTab;
				break;
			}
		}
		wasUpdated.current = true;
		if (combineApplied) {
			addSectionError(section, "finance");
			wasUpdated.current = false; // force user to fix error
		}
		updatedModel = clone(updatedModel);
		update(section, updatedModel);
	};

	const handleUpdateVariableGrossStructure = (section, key, data, isValid) => {
		handleValidation(section, key, isValid);
		let updateModel = { ...model };

		for (let i = 0; i < updateModel?.tabs?.length; i++) {
			if (updateModel?.tabs[i]?.key === key) {
				updateModel.tabs[i].variableGrossStructure = data;
				break;
			}
		}

		updateModel = clone(updateModel);

		if (key === "finance") {
			let localFinanceTab = updateModel?.tabs?.find((tab) => tab?.key === "finance");
			if (!data?.newFinance && !data?.preOwnedFinance) {
				localFinanceTab["combineOEMs"] = false;
				localFinanceTab["newAndPreOwnedRequired"] = false;
				localFinanceTab["globalUnitCounts"] = false;
				localFinanceTab["combinedOEMsGrossStructure"] = null;
			} else if (!data?.preOwnedFinance) {
				localFinanceTab["newAndPreOwnedRequired"] = false;
				if (!localFinanceTab["combinedOEMsGrossStructure"]?.length) {
					localFinanceTab["combinedOEMsGrossStructure"] = getOEMsGrossStructure(oems);
				}
			} else if (!data?.newFinance) {
				localFinanceTab["combinedOEMsGrossStructure"] = null;
				localFinanceTab["newAndPreOwnedRequired"] = false;
				if (!localFinanceTab?.globalUnitCounts) {
					localFinanceTab["combineOEMs"] = false;
				}
			} else if (data.newFinance && data?.preOwnedFinance && !localFinanceTab["combinedOEMsGrossStructure"]?.length) {
				localFinanceTab["combinedOEMsGrossStructure"] = getOEMsGrossStructure(oems);
			}

			localFinanceTab = updateFinanceMakes(localFinanceTab);
			for (let i = 0; i < model?.tabs?.length; i++) {
				if (updateModel.tabs[i]?.key === "finance") {
					updateModel.tabs[i] = localFinanceTab;
					break;
				}
			}
		}
		wasUpdated.current = true;
		const allUnchecked = data?.every?.((item) => !item?.checked);
		if (allUnchecked) {
			const tab = updateModel?.tabs?.find((otherTab) => otherTab?.key === key);
			tab?.makes?.forEach((make) => {
				make.tiers = newMatrix(make.make, make.applyTiersRetroactively, make.title, make.newAndPreOwnedRequired, make.makes)?.tiers;
			});
		}
		update(section, updateModel);
	};

	const handleUpdateCombinedGrossStructure = (section, key, data, isValid) => {
		let updateModel = { ...model };
		handleValidation(section, key, isValid);
		for (let i = 0; i < updateModel?.tabs?.length; i++) {
			if (updateModel?.tabs[i]?.key === key) {
				updateModel.tabs[i].combinedOEMsGrossStructure = data;
				break;
			}
		}
		wasUpdated.current = true;
		updateModel = clone(updateModel);
		update(section, updateModel);
	};
	const handleValidation = (section, key, value) => {
		value && !saveErrors?.current?.includes(key) ? removeSectionError(section, key) : addSectionError(section, key);
	};

	const updateFinanceMakes = (financeTabDefinition) => {
		const { makes, combineOEMs, globalUnitCounts, variableGrossStructure, newAndPreOwnedRequired } = financeTabDefinition || {};
		if (globalUnitCounts) {
			let globalMatrix = makes?.find((make) => make?.make === "global");
			if (!globalMatrix) {
				globalMatrix = newMatrix("global", false, "Total F&I Gross", false, [], true);
			}
			financeTabDefinition.makes = [globalMatrix];
			return clone(financeTabDefinition);
		} else {
			if (variableGrossStructure?.newFinance && !financeTabDefinition["combinedOEMsGrossStructure"]?.length) {
				financeTabDefinition["combinedOEMsGrossStructure"] = getOEMsGrossStructure(oems);
			}
		}

		let preOwnedFinanceMatrix = makes.find((make) => make.make === "preOwnedFinance");
		let newFinanceMatrix = makes.find((make) => make.make === "newFinance");

		const showNewFinanceGrossMatrix = variableGrossStructure?.newFinance && combineOEMs && !globalUnitCounts;
		const showPreOwnedFinanceMatrix = variableGrossStructure?.preOwnedFinance;
		const showOEMMatrices = variableGrossStructure?.newFinance && !combineOEMs;

		const newMakes = [];

		//"Total New F&I Gross" tab is shown when activated via variable gross structure or combine franchises switch
		if (showNewFinanceGrossMatrix) {
			if (!newFinanceMatrix) {
				newFinanceMatrix = newMatrix("newFinance", false, "Total New F&I Gross", newAndPreOwnedRequired, makes, true);
			}
			newMakes.push(newFinanceMatrix);
			if (!financeTabDefinition["combinedOEMsGrossStructure"]?.length && !globalUnitCounts) {
				financeTabDefinition["combinedOEMsGrossStructure"] = getOEMsGrossStructure(oems);
			}
		}
		if (showOEMMatrices) {
			const uniqueOEMs = setObjectsList(oems, ["oemReference"]);
			uniqueOEMs?.forEach((oem) => {
				let targetOemMatrix = makes?.find((make) => make?.make === oem?.oemReference && make?.dealerCodeFS === oem?.dealerCodeFS);

				if (!targetOemMatrix) {
					targetOemMatrix = newMatrix(
						oem?.oemReference,
						false,
						`Total New ${oem?.oemName} F&I Gross`,
						false,
						makes,
						true,
						oem?.dealerCodeFS
					);
				}
				newMakes.push(targetOemMatrix);
			});
		}
		if (showPreOwnedFinanceMatrix) {
			if (!preOwnedFinanceMatrix) {
				preOwnedFinanceMatrix = newMatrix(
					"preOwnedFinance",
					false,
					"Total Pre-Owned F&I Gross",
					newAndPreOwnedRequired,
					makes,
					true
				);
			}
			newMakes.push(preOwnedFinanceMatrix);
		}
		if (!variableGrossStructure?.newFinance) {
			financeTabDefinition["combinedOEMsGrossStructure"] = null;
		}
		financeTabDefinition.makes = newMakes;
		wasUpdated.current = true;
		return clone(financeTabDefinition);
	};

	const newMatrix = (
		make,
		applyTiersRetroactively = false,
		title = null,
		newAndPreOwnedRequired = false,
		makes = [],
		isFinance = false,
		dealerCodeFS = null
	) => {
		const tierMinValue = !isFinance ? -CONSTANTS.MAX_GROSS_INPUT : 0;
		const tierMaxValue = CONSTANTS.MAX_GROSS_INPUT;
		const columnMinValue = 0;
		const columnMaxValue = !isFinance ? CONSTANTS.MAX_COLUMNS_INPUT : CONSTANTS.DEFAULT_BACKEND_PRODUCTS;

		const matrix = DefaultCommissionsDefinition(
			make,
			[
				DefaultCommissionsTier(tierMinValue, tierMaxValue, [
					DefaultCommissionTier(columnMinValue, columnMaxValue, { commission: 0, input: "Percent" }),
				]),
			],
			{ applyTiersRetroactively }
		);
		if (title) {
			matrix["title"] = title;
		}
		if (newAndPreOwnedRequired && makes?.length) {
			let newMakes = [clone(matrix?.make)];
			const matrixDimensionsForTab = getMatrixDimensionsForTab({ makes });
			newMakes = resizeMakes(
				newMakes,
				{
					maxTiers: 1,
					minTiers: 1,
					maxColumns: 1,
					minColumns: 1,
				},
				matrixDimensionsForTab
			);
			matrix.make = newMakes[0];
		}
		if (isFinance) {
			matrix["afterChargebacks"] = true;
			if (dealerCodeFS) {
				matrix["dealerCodeFS"] = dealerCodeFS;
			}
		}
		return matrix;
	};

	const getCombinedTabs = (tabs = [], localFranchises = [], isCombined = false, newData = true) => {
		let combinedTabs = [];
		const previousTabs = [];

		if (!isCombined) {
			// toggle switch ON
			for (let i = 0; i < tabs?.length; i++) {
				if (tabs[i].key !== "newVehicles") {
					previousTabs.push(tabs[i]);
				}
			}

			localFranchises.forEach((franchise) => {
				const matrix = newData
					? newMatrix(franchise, false, "Total Variable Gross")
					: tabs
							?.find((item) => item?.key === "newVehicles")
							?.makes?.find((make) => String(make?.make).toLowerCase() === franchise.toLowerCase());
				const tab = {
					tabName: franchise,
					key: franchise,
					makes: [matrix],
				};
				combinedTabs.push(tab);
			});
		} else {
			tabs.forEach((tab) => {
				if (!localFranchises?.includes(tab?.key) && tab?.key !== "newVehicles") {
					previousTabs.push(tab);
				}
			});
			const newVehiclesTab = tabs?.find((tab) => tab?.key === "newVehicles") || {
				tabName: "New Vehicles",
				key: "newVehicles",
				makes: [newMatrix("newVehicles", false, "Total Variable Gross")],
				variableGrossStructure: localFranchises?.map((franchise) => {
					return { make: franchise, checked: false };
				}),
			};
			combinedTabs = [newVehiclesTab];
		}

		return [...combinedTabs, ...previousTabs];
	};

	const resizeMakes = (makes, localMatrixDimensions, newMatrixDimensions, makesDoNotModify = []) => {
		const unalteredMakes = makes.filter((element) => makesDoNotModify.includes(element?.make));
		makes = makes?.filter((element) => !makesDoNotModify.includes(element?.make));
		const tiersDifference =
			newMatrixDimensions.maxTiers - localMatrixDimensions.maxTiers || newMatrixDimensions.minTiers - localMatrixDimensions.minTiers;
		const columnsDifference =
			newMatrixDimensions.maxColumns - localMatrixDimensions.maxColumns ||
			newMatrixDimensions.minColumns - localMatrixDimensions.minColumns;
		if (columnsDifference !== 0 || tiersDifference !== 0) {
			if (tiersDifference >= 0 && columnsDifference >= 0) {
				// tiers got bigger
				// columns got bigger
				const { makes: expandedMakes, expandedKeys } = expandMakes({
					makes,
					sectionId,
					maximumColumnsInput,
					maxTiers: newMatrixDimensions.maxTiers,
					maxColumns: newMatrixDimensions.maxColumns,
				});
				const addKeys = expandedKeys.filter((key) => !saveErrors.current?.includes(key));
				saveErrors.current = [...saveErrors.current, ...addKeys];
				makes = expandedMakes;
			} else if (tiersDifference <= 0 && columnsDifference <= 0) {
				// tiers got smaller
				// columns got smaller
				const { makes: shrunkMakes } = shrinkMakes({
					makes,
					maximumColumnsInput,
					maxTiers: newMatrixDimensions.minTiers,
					maxColumns: newMatrixDimensions.minColumns,
				});
				makes = shrunkMakes;
			} else if (tiersDifference >= 0 && columnsDifference <= 0) {
				// tiers got bigger
				// columns got smaller
				const { makes: expandedMakes, expandedKeys } = expandMakes({
					makes,
					sectionId,
					maximumColumnsInput,
					maxTiers: newMatrixDimensions.maxTiers,
					maxColumns: newMatrixDimensions.maxColumns,
				});
				const addKeys = expandedKeys.filter((key) => !saveErrors.current?.includes(key));
				saveErrors.current = [...saveErrors.current, ...addKeys];
				makes = expandedMakes;

				const { makes: shrunkMakes } = shrinkMakes({
					makes,
					maximumColumnsInput,
					maxTiers: newMatrixDimensions.maxTiers,
					maxColumns: newMatrixDimensions.minColumns,
				});
				makes = shrunkMakes;
			} else if (tiersDifference <= 0 && columnsDifference >= 0) {
				// tiers got smaller
				// columns got bigger
				const { makes: shrunkMakes } = shrinkMakes({
					makes,
					maximumColumnsInput,
					maxTiers: newMatrixDimensions.minTiers,
					maxColumns: newMatrixDimensions.maxColumns,
				});
				makes = shrunkMakes;

				const { makes: expandedMakes, expandedKeys } = expandMakes({
					makes,
					sectionId,
					maximumColumnsInput,
					maxTiers: newMatrixDimensions.minTiers,
					maxColumns: newMatrixDimensions.maxColumns,
				});
				const addKeys = expandedKeys.filter((key) => !saveErrors.current?.includes(key));
				saveErrors.current = [...saveErrors.current, ...addKeys];
				makes = expandedMakes;
			}
		}
		return [...clone(makes), ...unalteredMakes];
	};

	const getModel = (localContent, localFranchises) => {
		const tabs = [];
		const contentIsNotNull = sanitizeObject(clone(localContent));
		if (localContent && contentIsNotNull) {
			for (const [key, value] of Object.entries(localContent)) {
				if (!tabSettings?.includes(key) && potentialKeys?.includes(key)) {
					let tabName = setTabNames.get(key);
					let makes = value?.["makes"] || [];
					let variableGrossStructure = value?.variableGrossStructure || null;
					if (value?.["makes"] && !tabName) {
						tabName = tabName || value?.["makes"]?.[0]?.make || "";
					} else if (value?.["make"]) {
						if (!tabName) {
							tabName = value["make"];
						}
						makes = [value];
						variableGrossStructure = value?.variableGrossStructure || null;
					}
					if (value) {
						const entries = Object.entries(value) || [];
						const potentialSwitches = {};
						entries.forEach(([entry, entryValue]) => {
							if (tabSettings.includes(entry)) {
								potentialSwitches[entry] = entryValue;
							}
						});
						const tabObj = {
							tabName,
							makes,
							variableGrossStructure,
							...potentialSwitches,
							key,
						};
						if (value["combinedOEMsGrossStructure"]) {
							tabObj["combinedOEMsGrossStructure"] = value["combinedOEMsGrossStructure"];
						}
						tabs.push(tabObj);
					}
				}
			}

			const localModel = {
				tabsProps: {
					newAndPreOwnedRequired: Boolean(localContent?.newAndPreOwnedRequired),
					combineFranchises: Boolean(localContent?.combineFranchises),
					globalUnitCounts: Boolean(localContent?.globalUnitCounts),
					isGuaranteedDraw: Boolean(localContent?.isGuaranteedDraw),
				},
				tabs,
			};

			if (!localContent?.combineFranchises) {
				localModel.tabs = getCombinedTabs(localModel.tabs, localFranchises, localContent?.combineFranchises, false);
			}
			return localModel;
		}

		return null;
	};

	React.useEffect(() => {
		const initModel = getModel(content, franchises);
		wasUpdated.current = false;
		saveErrors.current = [];
		setModel(initModel);
		return () => {
			setModel(null);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [content]);

	React.useEffect(() => {
		return () => {
			clearSectionErrors(sectionId);
		};
	}, [sectionId, clearSectionErrors]);

	React.useEffect(() => {
		const isValid = getIsValid(errors, sectionId);
		if (isValid && Boolean(model)) {
			update(sectionId, model, true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sectionId, model, errors]);

	return (
		<Grid container>
			<Grid item xs={12} mt={2.5}>
				{loading ? (
					<Grid>
						<TabLoader switch bottomSwitch />
						<DefaultTiersLoader mt={1} buttons />
					</Grid>
				) : (
					Boolean(model) && (
						<TabSelectable
							tabs={model?.tabs?.map((tab) => tab?.tabName) || []}
							tabSelected={tabIndex}
							onTabChange={(_, information) => setTabIndex(information?.index)}
							errors={tabsErrors}
							content={model?.tabs?.map((tab, index) => {
								const { key, ...tabsProps } = tab || {};
								return (
									<Grid container key={`${index}_${index}`}>
										<Grid item xs={12} className={"models-tiered-area"}>
											<CommissionSection
												{...tabsProps}
												readOnly={readOnly || loading}
												onActionUpdate={(tiers, isValid) => handleUpdateTiers(sectionId, key, tiers, isValid)}
												onActionValidation={(_make, isValid) => handleValidation(sectionId, key, isValid)}
												onVariableGrossStructureUpdate={(variableGrossStructure) =>
													handleUpdateVariableGrossStructure(sectionId, key, variableGrossStructure)
												}
												onCombinedGrossStructureUpdate={(variableGrossStructure) =>
													handleUpdateCombinedGrossStructure(sectionId, key, variableGrossStructure)
												}
												onFinanceSwitchUpdate={(area, value) => handleUpdateFinanceSwitch(sectionId, area, value)}
												newAndPreOwnedRequired={
													financeTabActive
														? financeTab?.newAndPreOwnedRequired
														: model?.tabsProps?.newAndPreOwnedRequired
												}
												financeGlobalUnitCounts={financeTab?.globalUnitCounts}
												matrixDimensions={matrixDimensions.get(key)}
												variableGrossStructure={tab?.variableGrossStructure || null}
												combinedOEMsGrossStructure={tab?.combinedOEMsGrossStructure || null}
												franchises={franchises}
												oems={oems}
												objectRef={key}
												tab={tab}
												tabsErrors={tabsErrors.map((error, errorIndex) => ({
													tabKey: model?.tabs?.[errorIndex]?.key,
													error,
												}))}
												backendProductsCount={CONSTANTS.DEFAULT_BACKEND_PRODUCTS}
												validateAgainst0={validateAgainst0}
											/>
										</Grid>
									</Grid>
								);
							})}
							topActions={
								financeTabActive
									? []
									: [
											<CustomizedSwitches
												key={"newAndPreOwnedRequired"}
												textLeft={true}
												textChecked={CONSTANTS.NEW_AND_PRE_OWNED_REQUIRED_LABEL}
												textUnChecked={CONSTANTS.NEW_AND_PRE_OWNED_REQUIRED_LABEL}
												checked={Boolean(model?.tabsProps?.newAndPreOwnedRequired)}
												disabled={readOnly || loading}
												onChange={(value) => handleUpdateSwitches(sectionId, "newAndPreOwnedRequired", value)}
												tooltip={{
													title: "Combines franchises if applicable and makes both tabs share the same number of rows and columns.",
													placement: "bottom",
													key: "default-commissions-new-and-preOwned-tooltip",
												}}
											/>,
											<CustomizedSwitches
												key={"combineFranchises"}
												textLeft={true}
												textChecked={"Combine Franchises"}
												textUnChecked={"Combine Franchises"}
												checked={Boolean(model?.tabsProps?.combineFranchises)}
												disabled={
													readOnly ||
													loading ||
													franchises?.length < 2 ||
													Boolean(model?.tabsProps?.newAndPreOwnedRequired)
												}
												onChange={(value) => handleUpdateSwitches(sectionId, "combineFranchises", value)}
												tooltip={{
													title: 'Turns all franchise tabs into a "New Vehicles" tab.',
													placement: "bottom",
													key: "default-commissions-combine-franchises-tooltip",
												}}
											/>,
											<CustomizedSwitches
												key={"globalUnitCounts"}
												textLeft={true}
												textChecked={"Global gross and units counts"}
												textUnChecked={"Global gross and units counts"}
												checked={Boolean(model?.tabsProps?.globalUnitCounts)}
												disabled={readOnly || loading || Boolean(model?.tabsProps?.newAndPreOwnedRequired)}
												onChange={(value) => handleUpdateSwitches(sectionId, "globalUnitCounts", value)}
												tooltip={{
													title: "Uses all units of New and Pre-Owned Vehicles together.",
													placement: "bottom",
													key: "default-commissions-global-unit-counts-tooltip",
												}}
											/>,
											<CustomizedSwitches
												key={"isGuaranteedDraw"}
												textLeft={true}
												textChecked={"Guaranteed Draw"}
												textUnChecked={"Guaranteed Draw"}
												checked={Boolean(model?.tabsProps?.isGuaranteedDraw)}
												disabled={readOnly || loading}
												onChange={(value) => handleUpdateSwitches(sectionId, "isGuaranteedDraw", value)}
												tooltip={{
													title: "Guarantee the draw.",
													placement: "bottom",
													key: "default-commissions-guarantee-draw-tooltip",
												}}
											/>,
									  ]
							}
							bottomActions={
								financeTabActive
									? [
											<CustomizedSwitches
												key={"newAndPreOwnedRequired"}
												textLeft={true}
												textChecked={CONSTANTS.NEW_AND_PRE_OWNED_REQUIRED_LABEL}
												textUnChecked={CONSTANTS.NEW_AND_PRE_OWNED_REQUIRED_LABEL}
												checked={Boolean(financeTab?.newAndPreOwnedRequired)}
												disabled={isDisabledSwitch({
													readOnly,
													loading,
													financeTab,
													franchises,
													switchKey: "newAndPreOwnedRequired",
												})}
												onChange={(value) => handleUpdateFinanceSwitch(sectionId, "newAndPreOwnedRequired", value)}
												tooltip={{
													title: "Combines franchises if applicable and makes all matrices in the tab share the same number of rows and columns.",
													placement: "top",
													key: "default-commissions-finance-new-and-preOwned-tooltip",
												}}
											/>,
											<CustomizedSwitches
												key={"combineOEMs"}
												textLeft={true}
												textChecked={"Combine OEMs"}
												textUnChecked={"Combine OEMs"}
												checked={Boolean(financeTab?.combineOEMs)}
												disabled={isDisabledSwitch({
													readOnly,
													loading,
													financeTab,
													franchises,
													switchKey: "combineOEMs",
												})}
												onChange={(value) => handleUpdateFinanceSwitch(sectionId, "combineOEMs", value)}
												tooltip={{
													title: "Turns all individual OEM matrices into one.",
													placement: "top",
													key: "default-commissions-finance-combine-franchises-tooltip",
												}}
											/>,
											<CustomizedSwitches
												key={"globalUnitCounts"}
												textLeft={true}
												textChecked={"Global Gross and Contracts"}
												textUnChecked={"Global Gross and Contracts"}
												checked={Boolean(financeTab?.globalUnitCounts)}
												disabled={isDisabledSwitch({
													readOnly,
													loading,
													financeTab,
													franchises,
													switchKey: "globalUnitCounts",
												})}
												onChange={(value) => handleUpdateFinanceSwitch(sectionId, "globalUnitCounts", value)}
												tooltip={{
													title: "Uses all units of New and Pre-Owned Vehicles together. Turns all matrices into one.",
													placement: "top",
													key: "default-commissions-finance-global-unit-counts-tooltip",
												}}
											/>,
									  ]
									: [
											<CustomizedSwitches
												key={"applyTiersRetroactively"}
												textLeft={true}
												textChecked={"Apply retroactively"}
												textUnChecked={"Apply retroactively"}
												checked={Boolean(model?.tabs?.[tabIndex]?.makes?.[0]?.applyTiersRetroactively)}
												disabled={readOnly || loading}
												onChange={(value) =>
													handleUpdateSwitches(sectionId, "applyTiersRetroactively", value, tabIndex)
												}
											/>,
									  ]
							}
						/>
					)
				)}
			</Grid>
		</Grid>
	);
}
DefaultCommissions.propTypes = {
	sectionId: PropTypes.string,
	content: PropTypes.object,
	franchises: PropTypes.array,
	oems: PropTypes.array,
	separateTabs: PropTypes.array,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	useSectionsErrors: PropTypes.func,
	onActionUpdate: PropTypes.func,
};
